import React, { useEffect, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
// eslint-disable-next-line import/named
import { RouteComponentProps } from 'react-router';

import { ErrorBoundary } from '../../App';
import { LoadingSpinner } from '../../components/layouts';
import { Menu } from '../../components/partials';
import { MenuActionsType } from '../../types/menu';
import { domUtils, urlUtils, utils } from '../../utils';
import * as Api from '../../weborder/Api';
import {
  DispatchProps,
  StateProps,
} from '../../weborder/component/MenuContainer/index';

type RouteParams = RouteComponentProps<
  {
    concessionStoreId?: number;
    id?: number;
    type?: 'collection' | 'delivery';
    vrid: number;
  },
  {}
>;

type Props = RouteParams & DispatchProps & StateProps;

const useStyles = (configHeaderOffset: number) => {
  const defaultHeaderOffset = 70;
  const headerOffset =
    configHeaderOffset > 0 ? configHeaderOffset : defaultHeaderOffset;

  return makeStyles((theme: Theme) => ({
    staticMenu: {
      minHeight: '100vh',
      paddingTop: headerOffset,
    },

    [theme.breakpoints.up('md')]: {
      staticMenu: {
        margin: theme.spacing(0, 'auto'),
        maxWidth: '960px',
      },
    },

    [theme.breakpoints.up('lg')]: {
      staticMenu: {
        maxWidth: '801px',
      },
    },
  }))();
};

export function StaticMenu(props: Props): JSX.Element {
  const {
    AddToExpandedState: addToExpandedState,
    appConfig,
    basket,
    CollectionSummaries: collectionSummaries,
    configHeaderOffset,
    DeliverySummaries: deliverySummaries,
    ExpandedState: expandedState,
    InitializeExpandedState: initializeExpandedState,
    Languages: languages,
    LoadRestaurant: loadRestaurant,
    menu,
    params,
    RemoveFromExpandedState: removeFromExpandedState,
    Restaurant: restaurant,
    translate,
    viewportTopOffset,
  } = props;

  const classes = useStyles(configHeaderOffset);

  const [loadRestaurantDetailStatus, setLoadRestaurantDetailStatus] = useState(
    'NOT_LOADED',
  );

  const languageCode = utils.get.activeLanguageCode(languages);

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  useEffect(() => {
    if (
      collectionSummaries.status === 'success' ||
      (deliverySummaries.status === 'success' &&
        loadRestaurantDetailStatus === 'NOT_LOADED')
    ) {
      loadRestaurant(props);
      setLoadRestaurantDetailStatus('LOADING');
    }
  }, [collectionSummaries.status, deliverySummaries.status]);

  useEffect(() => {
    if (restaurant.status === 'success') {
      setLoadRestaurantDetailStatus('LOADED');
    }
  }, [restaurant.status]);

  const handleClickItem = (): void => {
    urlUtils.navigateToHash('/where');
  };

  if (restaurant.status !== 'success' || menu === null) {
    return <LoadingSpinner />;
  }

  const currency = restaurant.data.Currency
    ? restaurant.data.Currency
    : Api.Currency.EUR;

  const menuActions = {
    onAddExpanded: addToExpandedState,
    onAddItem: handleClickItem,
    initExpanded: initializeExpandedState,
    onItemClick: handleClickItem,
    onRemoveExpanded: removeFromExpandedState,
    onRemoveItem: () => {},
  } as MenuActionsType;

  const staticMenuStyles = clsx(
    classes.staticMenu,
    'fd-page',
    'fd-page__static-menu',
  );

  const configData = appConfig.status === 'success' && appConfig.data;

  return (
    <ErrorBoundary identifier="static-menu">
      <div className={staticMenuStyles}>
        <Menu
          actions={menuActions}
          basket={basket}
          configData={configData}
          currency={currency}
          languageCode={languageCode}
          expandedState={expandedState}
          isStaticMenu
          menuData={menu}
          params={params}
          restaurantData={restaurant.data}
          translate={translate}
          viewportTopOffset={viewportTopOffset}
        />
      </div>
    </ErrorBoundary>
  );
}
